/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops セクション内のタブナビ。demo.html の app-nav（Queue / New Request / My Requests / Assignments / SLO）に、Queue の直後のシステム運用タブ（アラート由来のシステムオペレーション）を足して PIL の TanStack Router Link で再現し、アクティブタブをアクセントで示す。システム運用タブには未完了件数（scope=system のステート件数のうち完了以外）をバッジで添え、0 件なら出さない
 * @graph-connects useOpsStatusCounts [calls] システム運用タブの未完了件数（scope=system）の取得
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { useOpsStatusCounts } from '../../../hooks/useOps.js';

/** タブ定義。 */
interface OpsTabDef {
  /** 遷移先ルート。 */
  to: string;
  /** 表示ラベルの翻訳キー（module scope では hook を呼べないため t() で解決する）。 */
  labelKey: string;
  /** 完全一致でのみアクティブにするか（index ルート用）。 */
  exact?: boolean;
  /** システム運用タブか（未完了件数のバッジを添える）。 */
  systemOps?: boolean;
}

/** ops セクションのタブ一覧。システム運用は Queue の直後に置く（どちらも「対応する依頼の一覧」）。 @graph-connects none */
const OPS_TABS: OpsTabDef[] = [
  { to: '/ops', labelKey: 'ops.tabs.queue', exact: true },
  { to: '/ops/system', labelKey: 'ops.tabs.system', systemOps: true },
  { to: '/ops/submit', labelKey: 'ops.tabs.newRequest' },
  { to: '/ops/my-requests', labelKey: 'ops.tabs.myRequests' },
  { to: '/ops/assignments', labelKey: 'ops.tabs.assignments' },
  { to: '/ops/dashboard', labelKey: 'ops.tabs.slo' },
];

/**
 * システム運用タブの未完了件数バッジ。数えるのはサーバー（scope=system のステート件数）で、ここは
 * 完了以外を足すだけ。0 件・未取得のあいだは出さない（タブ列に常に「0」を並べない）。
 * @graph-connects useOpsStatusCounts [calls] scope=system のステート件数
 */
function SystemOpsOpenBadge(): ReactNode {
  const { t } = useTranslation();
  const counts = useOpsStatusCounts('all', undefined, 'system').data;
  const notClosed = counts === undefined ? 0 : counts.all - counts.closed;
  if (notClosed <= 0) return null;
  return (
    <span
      className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--color-bg-base)] px-1.5 text-xs font-semibold text-[var(--color-text-muted)]"
      aria-label={t('ops.tabs.systemOpenCount', { count: notClosed })}
    >
      {notClosed}
    </span>
  );
}

/** ops セクション内タブナビを描画する。 @graph-connects SystemOpsOpenBadge [calls] システム運用タブの未完了件数 */
export function OpsTabs(): ReactNode {
  const { t } = useTranslation();
  return (
    <nav className="flex flex-wrap gap-1 border-b border-[var(--color-border)] pb-2">
      {OPS_TABS.map((tab) => (
        <Link
          key={tab.to}
          to={tab.to}
          activeOptions={{ exact: tab.exact === true }}
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]"
          activeProps={{
            className:
              'rounded-lg px-3 py-1.5 text-sm font-semibold bg-[var(--color-accent-primary)] text-white',
          }}
        >
          {t(tab.labelKey)}
          {tab.systemOps === true && <SystemOpsOpenBadge />}
        </Link>
      ))}
    </nav>
  );
}
